import type { ReactNode } from "react";

import { Icon, type IconName } from "@/components/common/Icon";

export type OnbStepDef = {
  id: string;
  label: string;
  short: string;
  required: boolean;
  icon: IconName;
};

interface Props {
  stepId: string;
  STEPS: OnbStepDef[];
  completed: Record<string, boolean>;
  skipped: Record<string, boolean>;
  onGoStep: (id: string) => void;
  completedCount: number;
  onExit: () => void;
  children: ReactNode;
}

export function OnbShell({
  stepId,
  STEPS,
  completed,
  skipped,
  onGoStep,
  completedCount,
  onExit,
  children,
}: Props) {
  return (
    <div className="onb-shell">
      <header className="onb-topbar">
        <div className="onb-brand">
          <span
            className="ws-mark"
            style={{
              background: "linear-gradient(135deg, #F4CC04, #B49A02)",
              color: "#011D33",
            }}
          >
            TD
          </span>
          <div className="onb-brand-text">
            <div className="onb-brand-name">Tributo Devido</div>
            <div className="onb-brand-sub">Cadastro de workspace</div>
          </div>
        </div>
        <span
          className="onb-preview-tag"
          title="Esta é a simulação do que o cliente vê — você está no modo preview"
        >
          <Icon name="sparkles" size={11} /> PREVIEW · simulação do cliente
        </span>
        <button className="onb-exit-btn" onClick={onExit}>
          <Icon name="log-out" size={14} /> Salvar e sair
        </button>
      </header>

      <aside className="onb-stepper">
        <div className="onb-stepper-head">
          <div className="onb-stepper-eyebrow">
            Onboarding · {STEPS.length} etapas
          </div>
          <div className="onb-stepper-title">
            Vamos preparar
            <br />
            seu workspace
          </div>
          <div className="onb-stepper-progress">
            <div className="onb-progress-bar">
              <div
                className="onb-progress-fill"
                style={{ width: `${(completedCount / STEPS.length) * 100}%` }}
              />
            </div>
            <div className="onb-progress-text">
              {completedCount}/{STEPS.length} concluídas
            </div>
          </div>
        </div>
        <ol className="onb-steps">
          {STEPS.map((s, i) => {
            const isCompleted = !!completed[s.id];
            const isSkipped = !!skipped[s.id];
            const isActive = stepId === s.id;
            const cls = [
              "onb-step",
              isActive ? "is-active" : "",
              isCompleted ? "is-completed" : "",
              isSkipped && !isCompleted ? "is-skipped" : "",
            ]
              .filter(Boolean)
              .join(" ");
            return (
              <li key={s.id}>
                <button className={cls} onClick={() => onGoStep(s.id)}>
                  <span className="onb-step-num">
                    {isCompleted ? <Icon name="check" size={14} stroke={3} /> : i + 1}
                  </span>
                  <span className="onb-step-info">
                    <span className="onb-step-label">{s.label}</span>
                    <span className="onb-step-meta">
                      {isCompleted
                        ? "Concluída"
                        : isSkipped
                          ? "Pulada · pode completar depois"
                          : isActive
                            ? "Em andamento"
                            : s.required
                              ? "Obrigatória"
                              : "Opcional"}
                    </span>
                  </span>
                  {s.required && !isCompleted && (
                    <span className="onb-step-req">Obrig.</span>
                  )}
                </button>
              </li>
            );
          })}
        </ol>
        <div className="onb-stepper-foot">
          <Icon name="shield-check" size={12} />
          <span>Seus dados são criptografados e armazenados conforme LGPD.</span>
        </div>
      </aside>

      <main className="onb-content">
        <div className="onb-inner">{children}</div>
      </main>
    </div>
  );
}
